import { useState } from "react";
import { Lock, Wallet, ExternalLink, Loader2, WifiOff, RefreshCw, Undo2, CloudUpload } from "lucide-react";
import { formatCurrency } from "../../lib/utils";

const PendingClose = ({ pendingClose, pendingSales, online, syncing, onSync, onDiscardClose }) => (
  <div className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-[#1b1b1b] shadow-2xl overflow-hidden" data-testid="pos-cash-pending-close">
    <div className="bg-amber-500/15 border-b border-amber-500/30 px-6 py-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
        {online ? <CloudUpload className="w-6 h-6 text-black" /> : <WifiOff className="w-6 h-6 text-black" />}
      </div>
      <div>
        <p className="text-lg font-black text-amber-300 tracking-wide" data-testid="pos-cash-pending-close-title">CAIXA FECHADA OFFLINE</p>
        <p className="text-xs text-zinc-400">{online ? "A conciliar com o servidor…" : "À espera de ligação para conciliar os movimentos com o servidor."}</p>
      </div>
    </div>
    <div className="px-6 py-5 space-y-3 text-sm text-zinc-300">
      <p>Fecho registado às <strong className="text-white">{new Date(pendingClose.closed_at).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" })}</strong> · contado <strong className="text-white font-mono" data-testid="pos-cash-pending-counted">{formatCurrency(pendingClose.closing_balance)}</strong> · esperado neste terminal <span className="font-mono">{formatCurrency(pendingClose.expected_cash_local || 0)}</span>.</p>
      {pendingSales > 0 && <p className="text-amber-300" data-testid="pos-cash-pending-sales">{pendingSales} venda(s) offline ainda por sincronizar — o fecho é enviado depois de todas as vendas entrarem no servidor.</p>}
      {pendingClose.error && <p className="text-red-400" data-testid="pos-cash-pending-error">Erro na conciliação: {pendingClose.error}</p>}
      <div className="flex flex-wrap gap-2 pt-1">
        {online && <button onClick={onSync} disabled={syncing} className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-sm flex items-center gap-2" data-testid="pos-cash-pending-sync"><RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Conciliar agora</button>}
        <button onClick={() => { if (window.confirm("Anular o fecho offline? A caixa volta a ficar aberta neste terminal (nenhuma venda é perdida).")) onDiscardClose(); }} className="h-10 px-4 rounded-lg bg-[#262626] hover:bg-[#333] text-zinc-200 text-sm flex items-center gap-2" data-testid="pos-cash-pending-discard"><Undo2 className="w-4 h-4" /> Anular fecho offline</button>
      </div>
    </div>
  </div>
);

export const POSCashLockOverlay = ({ onOpen, busy, offline = false, pendingClose = null, pendingSales = 0, online = true, syncing = false, onSync, onDiscardClose }) => {
  const [balance, setBalance] = useState("");
  const [notes, setNotes] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (balance === "" || Number(balance) < 0) return;
    onOpen(Number(balance), notes);
  };
  return (
    <div className="absolute inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pos-cash-lock">
      {pendingClose ? (
        <PendingClose pendingClose={pendingClose} pendingSales={pendingSales} online={online} syncing={syncing} onSync={onSync} onDiscardClose={onDiscardClose} />
      ) : (
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-red-500/30 bg-[#1b1b1b] shadow-2xl overflow-hidden">
        <div className="bg-red-600/15 border-b border-red-500/30 px-6 py-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shrink-0">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-lg font-black text-red-300 tracking-wide" data-testid="pos-cash-lock-title">CAIXA FECHADO</p>
            <p className="text-xs text-zinc-400">Não é possível registar vendas, pedidos ou pagamentos sem caixa aberto.</p>
          </div>
        </div>
        <div className="px-6 py-5 space-y-4">
          {offline && (
            <p className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-2.5 text-xs text-red-200" data-testid="pos-cash-open-offline-hint">
              <WifiOff className="w-4 h-4 shrink-0 animate-pulse" /> Sem ligação: a abertura fica registada neste dispositivo e é enviada ao servidor quando a ligação voltar. As vendas offline entram nesta caixa.
            </p>
          )}
          <p className="text-sm text-zinc-300">Para começar a vender indique o <strong className="text-white">fundo de caixa inicial</strong> (dinheiro que está na gaveta) e abra o caixa. A abertura fica registada com o seu nome.</p>
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-wide text-zinc-400">Fundo de caixa inicial (Kz)</label>
            <div className="relative">
              <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="number"
                min="0"
                step="0.01"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                placeholder="0,00"
                autoFocus
                className="w-full h-12 pl-10 pr-3 rounded-lg bg-[#262626] border border-white/10 text-lg font-mono text-zinc-100 focus:outline-none focus:border-emerald-500"
                data-testid="pos-cash-open-balance"
              />
            </div>
          </div>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observações (opcional)"
            className="w-full h-9 px-3 rounded-lg bg-[#262626] border border-white/10 text-sm text-zinc-200 placeholder:text-zinc-500 focus:outline-none focus:border-emerald-500"
            data-testid="pos-cash-open-notes"
          />
          <button
            type="submit"
            disabled={busy || balance === ""}
            className="w-full h-12 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="pos-cash-open-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : offline ? <WifiOff className="w-4 h-4" /> : <Wallet className="w-4 h-4" />}
            {offline ? "Abrir Caixa OFFLINE e começar a vender" : "Abrir Caixa e começar a vender"}
          </button>
          <a href="/cash" className="flex items-center justify-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors" data-testid="pos-cash-page-link">
            <ExternalLink className="w-3.5 h-3.5" /> Ir para a página Caixa (histórico e relatórios)
          </a>
        </div>
      </form>
      )}
    </div>
  );
};
